import { useState, useRef, useCallback } from 'react';
import type { SleepSound } from '@parentpath/supabase-client';
import { useTranslation } from '@parentpath/i18n';

interface SoundCarouselProps {
  sounds: SleepSound[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  getPublicUrl: (bucket: string, path: string) => string;
  isPlaying: boolean;
}

const ACTIVE_SIZE_VW = 65;
const INACTIVE_SCALE = 0.615; // ~40vw visual size
const OFFSET_VW = 43; // center-to-center offset for inactive items
const SWIPE_THRESHOLD = 50; // px

export function SoundCarousel({
  sounds,
  currentIndex,
  onIndexChange,
  getPublicUrl,
  isPlaying,
}: SoundCarouselProps) {
  const { i18n } = useTranslation();
  const locale = i18n.language as 'de' | 'en';
  const touchStartX = useRef(0);
  const [dragOffset, setDragOffset] = useState(0);
  const isDragging = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    isDragging.current = true;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const diff = e.touches[0].clientX - touchStartX.current;
    setDragOffset(diff);
  }, []);

  const handleTouchEnd = useCallback(() => {
    isDragging.current = false;
    if (Math.abs(dragOffset) > SWIPE_THRESHOLD) {
      if (dragOffset < 0) {
        // Swiped left -> next
        onIndexChange((currentIndex + 1) % sounds.length);
      } else {
        // Swiped right -> prev
        onIndexChange((currentIndex - 1 + sounds.length) % sounds.length);
      }
    }
    setDragOffset(0);
  }, [dragOffset, currentIndex, sounds.length, onIndexChange]);

  if (sounds.length === 0) return null;

  const prevIndex = (currentIndex - 1 + sounds.length) % sounds.length;
  const nextIndex = (currentIndex + 1) % sounds.length;
  const currentSound = sounds[currentIndex];
  const prevSound = sounds.length > 1 ? sounds[prevIndex] : null;
  const nextSound = sounds.length > 1 ? sounds[nextIndex] : null;

  const getTitle = (sound: SleepSound) => {
    return locale === 'en' ? sound.title_en : sound.title_de;
  };

  // Drag progress: -1 (swiped fully left) to +1 (swiped fully right)
  const maxDragPx = (typeof window !== 'undefined' ? window.innerWidth : 400) * 0.35;
  const dragProgress = Math.max(-1, Math.min(1, dragOffset / maxDragPx));

  // Compute transform/opacity for an item based on its effective slot
  // slot 0 = center (active), slot ±1 = side (inactive)
  const getItemStyle = (baseSlot: number) => {
    const slot = baseSlot + dragProgress;
    const absSlot = Math.min(Math.abs(slot), 1.5);

    const translateX = slot * OFFSET_VW;
    const scale = Math.max(0.3, 1 - absSlot * (1 - INACTIVE_SCALE));
    const opacity = Math.max(0.1, 1 - absSlot * 0.5);

    return {
      transform: `translateX(-50%) translateY(-50%) translateX(${translateX}vw) scale(${scale})`,
      opacity,
    };
  };

  const transition = !isDragging.current
    ? 'transform 300ms ease-out, opacity 300ms ease-out'
    : 'none';

  return (
    <div
      className="relative overflow-hidden w-full select-none"
      style={{ height: `${ACTIVE_SIZE_VW + 10}vw` }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Previous Sound */}
      {prevSound && (
        <button
          onClick={() => onIndexChange(prevIndex)}
          className="absolute top-1/2 left-1/2 rounded-full overflow-hidden"
          style={{
            width: `${ACTIVE_SIZE_VW}vw`,
            height: `${ACTIVE_SIZE_VW}vw`,
            zIndex: 1,
            transition,
            ...getItemStyle(-1),
          }}
        >
          <img
            src={getPublicUrl('SleepSoundImages', prevSound.image_path)}
            alt={getTitle(prevSound)}
            className="w-full h-full object-cover"
          />
        </button>
      )}

      {/* Current Sound */}
      <div
        className="absolute top-1/2 left-1/2"
        style={{
          width: `${ACTIVE_SIZE_VW}vw`,
          zIndex: 2,
          transition,
          ...getItemStyle(0),
        }}
      >
        <div
          className={`w-full rounded-full overflow-hidden border-2 ${
            isPlaying
              ? 'border-pp-orange shadow-lg shadow-pp-orange/25'
              : 'border-white/25'
          }`}
          style={{
            height: `${ACTIVE_SIZE_VW}vw`,
            background: 'rgba(15, 25, 35, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <img
            src={getPublicUrl('SleepSoundImages', currentSound.image_path)}
            alt={getTitle(currentSound)}
            className={`w-full h-full object-cover transition-transform duration-700 ${
              isPlaying ? 'scale-110' : 'scale-100'
            }`}
          />
        </div>
        <p className="text-white text-sm font-semibold text-center mt-3">
          {getTitle(currentSound)}
        </p>
      </div>

      {/* Next Sound */}
      {nextSound && (
        <button
          onClick={() => onIndexChange(nextIndex)}
          className="absolute top-1/2 left-1/2 rounded-full overflow-hidden"
          style={{
            width: `${ACTIVE_SIZE_VW}vw`,
            height: `${ACTIVE_SIZE_VW}vw`,
            zIndex: 1,
            transition,
            ...getItemStyle(1),
          }}
        >
          <img
            src={getPublicUrl('SleepSoundImages', nextSound.image_path)}
            alt={getTitle(nextSound)}
            className="w-full h-full object-cover"
          />
        </button>
      )}
    </div>
  );
}
